import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CompanyState,
  CompanyStatusData,
  ImportDataStatus,
  IntegrationData,
  MismatchData,
  UserRoles,
} from "../types/companyTypes";
import { UpdatedAccountListItem } from "../types";
import { AccountPlanStatus } from "../types/enums";

const initStatus = {
  analysis_status: undefined,
  scraping_status: undefined,
  scraping_update_status: undefined,
  generation_status: undefined,
  account_plan_status: undefined,
};

const initialState: CompanyState = {
  isFetchTableMembers: false,
  discardTableChanges: false,
  account_id: "",
  company: "",
  analysisStatus: "idle",
  loadingAnalyse: "",
  uploadModal: "",
  update: "",
  updateAnalytics: false,
  integrationData: null,
  createProductAnalyses: false,
  status: initStatus,
  recalculation_status: "",
  mismatchData: null,
  importDataStatus: null,
  isNotificationsOpen: false,
  updatedCompanies: {},
  userRoles: {
    isOwner: false,
    isAdmin: false,
    isEditor: undefined,
    isOnlyViewer: false,
    userEmail: "",
  },
  isModalOpen: false,
  removedID: "",
  updatedStatus: {},
};

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    setUpdate: (state, action: PayloadAction<string>) => {
      state.update = action.payload;
    },
    clearCompany: (state) => {
      state.company = "";
    },
    setUploadModal: (state, action: PayloadAction<string>) => {
      state.uploadModal = action.payload;
    },
    setIntegrationData: (
      state,
      action: PayloadAction<IntegrationData | null>,
    ) => {
      state.integrationData = action.payload;
    },
    setCreateProductAnalyses: (state, action: PayloadAction<boolean>) => {
      state.createProductAnalyses = action.payload;
    },
    setStatus: (state, action: PayloadAction<CompanyStatusData>) => {
      state.status = { ...state.status, ...action.payload };
    },
    setRecalculatingStatus: (state, action: PayloadAction<string>) => {
      state.recalculation_status = action.payload;
    },
    setMismatchData: (state, action: PayloadAction<MismatchData | null>) => {
      state.mismatchData = action.payload;
    },
    setImportDataStatus: (
      state,
      action: PayloadAction<ImportDataStatus | null>,
    ) => {
      state.importDataStatus = action.payload;
    },
    setIsNotificationsOpen: (state, action: PayloadAction<boolean>) => {
      state.isNotificationsOpen = action.payload;
    },
    setIsFetchTableMembers: (state, action: PayloadAction<boolean>) => {
      state.isFetchTableMembers = action.payload;
    },
    setDiscardTableChanges: (state, action: PayloadAction<boolean>) => {
      state.discardTableChanges = action.payload;
    },

    setUpdatedCompanies: (
      state,
      action: PayloadAction<UpdatedAccountListItem>,
    ) => {
      const { id, ...rest } = action.payload;
      const filtered = Object.fromEntries(
        Object.entries(rest).filter(([_, v]) => v !== undefined),
      );

      state.updatedCompanies[id] = {
        ...(state.updatedCompanies[id] ?? {}),
        ...filtered,
      };
    },

    clearUpdatedCompanies: (state) => {
      state.updatedCompanies = {};
    },

    removeUpdatedCompany: (state, action: PayloadAction<string>) => {
      delete state.updatedCompanies[action.payload];
    },

    setUserRole: (state, action: PayloadAction<UserRoles>) => {
      state.userRoles = action.payload;
    },

    setOpenModal: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },

    setRemovedId: (state, action: PayloadAction<string>) => {
      state.removedID = action.payload;
    },

    setUpdatedStatus: (
      state,
      action: PayloadAction<{ [key: string]: AccountPlanStatus }>,
    ) => {
      state.updatedStatus = {
        ...state.updatedStatus,
        ...action.payload,
      };
    },
  },
});

export const {
  clearCompany,
  setUploadModal,
  setUpdate,
  setIntegrationData,
  setCreateProductAnalyses,
  setStatus,
  setRecalculatingStatus,
  setMismatchData,
  setImportDataStatus,
  setIsNotificationsOpen,
  setIsFetchTableMembers,
  setDiscardTableChanges,
  setUpdatedCompanies,
  clearUpdatedCompanies,
  removeUpdatedCompany,
  setUserRole,
  setOpenModal,
  setRemovedId,
  setUpdatedStatus,
} = companySlice.actions;
export default companySlice.reducer;
